/**
 * useNavFilters — vereinheitlichter Filter-State für das 4-Achsen-Modell (Migration 0010).
 *
 * Speichert den vollständigen Filter-Zustand in URL-Query-Params, damit:
 *   - Links bookmarkbar sind
 *   - Tree-Klicks die Filter-State direkt setzen können
 *   - Drill-Down zwischen Seiten verlustfrei navigiert
 *
 * Verwendet von: ArchivPage, InboxPage, ZuletztPage, AuswertungenPage, DrillDownPage,
 * AxisTree. Die Achsen-Parameter überlappen mit denen aus `useReportByCategory` —
 * `toReportParams()` extrahiert die für die Reports-API.
 *
 * ⚠️ Wichtige Semantik: ein nicht gesetzter Parameter = „alle", nicht „keine".
 */
import { useSearchParams } from "react-router-dom";
import { useCallback, useMemo } from "react";
import type { ReportParams } from "./receipts";

export type Direction = "expense" | "income" | "neutral";
export type StabilityFilter = "fixed" | "variable" | "none";

export type NavFilters = {
  from?: string;          // YYYY-MM-DD
  to?: string;            // YYYY-MM-DD
  gruppe?: string;        // S3: Gruppe der Buchungsart (ersetzt usageType)
  nurUnbestaetigt?: string; // "1" = nur unbestätigte Buchungsarten (Vorschläge)
  direction?: Direction;
  stability?: StabilityFilter;
  categoryId?: string;    // <uuid> | "none"
  subcategoryId?: string;
  subSubcategoryId?: string;
  vendor?: string;
  search?: string;
};

const KEYS = [
  "from",
  "to",
  "gruppe",
  "nurUnbestaetigt",
  "direction",
  "stability",
  "categoryId",
  "subcategoryId",
  "subSubcategoryId",
  "vendor",
  "search",
] as const satisfies readonly (keyof NavFilters)[];

function readFilters(sp: URLSearchParams): NavFilters {
  const out: NavFilters = {};
  for (const k of KEYS) {
    const v = sp.get(k);
    if (v !== null && v !== "") {
      // Typsicherheit auf Aufrufer-Seite — wir vertrauen der URL, validieren beim Setzen.
      (out as Record<string, string>)[k] = v;
    }
  }
  return out;
}

function writeFilters(prev: URLSearchParams, next: NavFilters): URLSearchParams {
  const out = new URLSearchParams(prev);
  for (const k of KEYS) {
    const v = next[k];
    if (v === undefined || v === null || v === "") out.delete(k);
    else out.set(k, String(v));
  }
  return out;
}

export function useNavFilters() {
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(() => readFilters(searchParams), [searchParams]);

  // Achse umschalten — setzt entweder oder löscht (Toggle-Verhalten beim Klick auf gleichen Knoten).
  const toggle = useCallback(
    <K extends keyof NavFilters>(key: K, value: NonNullable<NavFilters[K]>) => {
      setSearchParams(
        (prev) => {
          const cur = prev.get(key);
          const next = { ...readFilters(prev) };
          if (cur === String(value)) {
            delete next[key];
          } else {
            (next as Record<string, unknown>)[key] = value;
          }
          return writeFilters(prev, next);
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  // Mehrere Achsen auf einmal setzen (z.B. Tree-Click mit kombiniertem Pfad).
  // `null` als Wert löscht den Parameter.
  const patch = useCallback(
    (delta: Partial<{ [K in keyof NavFilters]: NavFilters[K] | null }>) => {
      setSearchParams(
        (prev) => {
          const next = { ...readFilters(prev) };
          for (const [k, v] of Object.entries(delta) as [keyof NavFilters, unknown][]) {
            if (v === undefined || v === null || v === "") delete next[k];
            else (next as Record<string, unknown>)[k] = v;
          }
          return writeFilters(prev, next);
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const reset = useCallback(() => {
    setSearchParams((prev) => writeFilters(prev, {}), { replace: true });
  }, [setSearchParams]);

  return { filters, toggle, patch, reset };
}

// Extrahiert die für die Reports-API relevanten Felder (1:1 mit ReportParams).
export function toReportParams(f: NavFilters): ReportParams {
  return {
    from: f.from,
    to: f.to,
    gruppe: f.gruppe,
    nurUnbestaetigt: f.nurUnbestaetigt === "1",
    direction: f.direction,
    stability: f.stability,
  };
}
